// Pure-TS store backing the LedgerContext (`./LedgerContext.tsx`).
// Owns ref-counted shared subscriptions, dedupe by id, cursor-based catch-up
// on reconcile (focus / visibilitychange / interval), and stable item-array
// references for memoization. Per ADR 009.

import type { RealtimeSubscriber } from '../../types/realtime/subscriber';
import { redactTopicSecret } from '../../isomorphic/realtime/topics';
import type { Unsubscribe } from '../../types/realtime/types';
import { formatRealtimeFailure } from '../../isomorphic/realtime/realtime-failure-log';

/**
 * Items in a ledger are immutable and identified by `id`.
 *
 * Ordering comes from one of two keys, and which one a topic uses is a property
 * of the topic, not of an individual item — every item on a given topic carries
 * the same shape.
 *
 * `seq` is a **per-topic** monotonic sequence: dense, 1-based, and therefore a
 * gap DETECTOR as well as an ordering key — `N+1` is the next item, `N+2` means
 * one was missed. The two bid ledgers carry it (BL-1631).
 *
 * When `seq` is absent the store falls back to `(createdAt, id)`. That pair
 * orders correctly but cannot detect a gap, and it cannot be a sound catch-up
 * cursor over a table whose timestamps are transaction-START times — which is
 * exactly why the bid ledgers moved off it. It remains correct for
 * `customer-user:{id}:toasts:ledger:{token}`, which is live-only (no reconciler, no
 * cursor — see `CustomerToastListener`) and best-effort by design, so PRJ-65
 * deliberately left it alone.
 *
 * A NEW ledger topic should carry `seq`. The fallback exists for the toast
 * ledger, not as a choice for new work.
 */
export interface LedgerItem {
    id: string;
    createdAt: string;
    /** Per-topic monotonic sequence. Absent on topics that have not adopted one. */
    seq?: number;
}

/**
 * Cursor for catch-up requests, in whichever key its topic orders by.
 *
 * `{ seq }` — "items with `seq > cursor.seq`". `{ createdAt, id }` — "items with
 * `(createdAt, id) > (cursor.createdAt, cursor.id)`".
 */
export type LedgerCursor = { seq: number } | { createdAt: string; id: string };

/** Narrow a cursor to its sequence form. */
export function isSeqCursor(cursor: LedgerCursor): cursor is { seq: number } {
    return 'seq' in cursor;
}

/**
 * Reconciler invoked on reconnect / focus / visibilitychange / interval to
 * close any gaps between what the client has and what the server has.
 * Returning items already known to the client is fine — dedupe handles it.
 */
export type LedgerReconciler<T extends LedgerItem> = (
    cursor: LedgerCursor | undefined,
) => Promise<T[]>;

/** Initial-fetch fn — invoked once by the FIRST subscriber for a topic. */
export type LedgerInitialFetcher<T extends LedgerItem> = () => Promise<T[]>;

/**
 * Lifecycle of a topic's initial fetch (BUG-803). This is a per-TOPIC fact, not
 * a per-subscriber one: the store runs `initialFetch` once for the first
 * subscriber, so a second subscriber (e.g. the "All Bidding Activity" modal that
 * opens over an inline panel already on the topic) never ran a fetch of its own.
 * Tracking the status here lets every subscriber — first or late — observe the
 * same `loaded`/`error`, instead of a late subscriber sitting on a spinner that
 * nothing will ever clear.
 */
export type LedgerFetchStatus = 'loading' | 'loaded' | 'error';

/** Shared per-topic initial-fetch state, read via `useLedgerFetchState`. */
export interface LedgerFetchState {
    status: LedgerFetchStatus;
    /** Populated only when `status === 'error'` (the value the fetcher threw). */
    error: Error | null;
}

/** Frozen default returned for a topic with no entry yet (pre-subscribe). */
const LOADING_FETCH_STATE: Readonly<LedgerFetchState> = Object.freeze({
    status: 'loading',
    error: null,
});

interface LedgerEntry {
    items: LedgerItem[];
    itemsById: Map<string, LedgerItem>;
    cursor: LedgerCursor | undefined;
    subscribers: number;
    /** Combined unsubscribe for the per-topic appended + amended subscriptions. */
    transportUnsubscribe: Unsubscribe | undefined;
    reconciler: LedgerReconciler<LedgerItem> | undefined;
    reconciling: boolean;
    /**
     * BL-2342: an initial fetch held back because the transport was suspended
     * for offline mode when the topic mounted. The first reconcile after the
     * suspension lifts runs it.
     */
    heldInitialFetch?: LedgerInitialFetcher<LedgerItem>;
    listeners: Set<() => void>;
    /** Shared initial-fetch lifecycle (BUG-803). Stable object reference;
     *  recreated only on change so `useSyncExternalStore` caches correctly. */
    fetchState: Readonly<LedgerFetchState>;
}

/**
 * Compare two ledger items by their topic's ordering key.
 *
 * When BOTH carry a per-topic `seq` (BL-1631) that single key decides it — it is
 * monotonic and dense, so no tiebreak can be needed. Otherwise fall back to
 * `(createdAt, id)` lexicographically: `createdAt` first (ISO-8601 strings sort
 * correctly as strings), then `id` as tiebreaker.
 *
 * Requiring BOTH to carry `seq` is deliberate rather than defensive. A topic is
 * homogeneous — every item on a sequenced topic has one — so the mixed case
 * only arises transiently while a tab holds items fetched across a deploy that
 * added the sequence. Falling back there keeps the order stable instead of
 * sorting the unsequenced items into an arbitrary position.
 */
function compareItems(a: LedgerItem, b: LedgerItem): number {
    if (a.seq !== undefined && b.seq !== undefined) {
        return a.seq - b.seq;
    }
    if (a.createdAt < b.createdAt) return -1;
    if (a.createdAt > b.createdAt) return 1;
    if (a.id < b.id) return -1;
    if (a.id > b.id) return 1;
    return 0;
}

/**
 * Compare a candidate cursor against the one held, in whichever key they share.
 * Returns a positive number when `next` is strictly ahead of `held`.
 *
 * A cursor never changes shape mid-topic; if it somehow did, the store treats
 * the new one as ahead rather than silently refusing to advance for the life of
 * the tab (the BUG-1281 failure mode).
 */
function compareCursors(next: LedgerCursor, held: LedgerCursor): number {
    if (isSeqCursor(next) && isSeqCursor(held)) return next.seq - held.seq;
    if (isSeqCursor(next) || isSeqCursor(held)) return 1;
    if (next.createdAt !== held.createdAt) return next.createdAt < held.createdAt ? -1 : 1;
    if (next.id !== held.id) return next.id < held.id ? -1 : 1;
    return 0;
}

/**
 * Pure-TS ledger store.
 *
 * @sideEffect Opens transport subscriptions; invokes user-supplied fetchers
 *   and reconcilers; logs failed reconciles to the console (does not throw).
 */
export class LedgerStore {
    private readonly entries = new Map<string, LedgerEntry>();

    /**
     * Fetch-state observers, kept SEPARATE from the item-subscription
     * ref-count path (`entries[*].listeners`) so subscribing to a topic's
     * fetch state never opens a transport channel, runs an initial fetch, or
     * affects the subscriber count. Purely additive over the live-bidding flow.
     */
    private readonly fetchStateListeners = new Map<string, Set<() => void>>();

    constructor(private readonly subscriber: RealtimeSubscriber) {}

    /**
     * Attach a listener for `topic`. First subscriber opens the transport
     * channel, runs the initial fetch (if any), and captures the reconciler
     * for later focus / visibilitychange / interval calls.
     */
    subscribe<T extends LedgerItem>(
        topic: string,
        listener: () => void,
        initialFetch: LedgerInitialFetcher<T> | undefined,
        reconcile: LedgerReconciler<T> | undefined,
    ): Unsubscribe {
        let entry = this.entries.get(topic);
        if (!entry) {
            entry = {
                items: [],
                itemsById: new Map(),
                cursor: undefined,
                subscribers: 0,
                transportUnsubscribe: undefined,
                reconciler: undefined,
                reconciling: false,
                listeners: new Set(),
                fetchState: LOADING_FETCH_STATE,
            };
            this.entries.set(topic, entry);
        }

        const isFirstSubscriber = entry.subscribers === 0;
        entry.listeners.add(listener);
        entry.subscribers++;
        // Capture the reconciler from the first subscriber for later calls.
        // Late arrivers' reconcilers are ignored — the topic has one shape.
        if (isFirstSubscriber && reconcile) {
            entry.reconciler = reconcile as LedgerReconciler<LedgerItem>;
        }

        if (isFirstSubscriber) {
            this.openTransportSubscription(topic);
            if (initialFetch) {
                // Entry already defaults to `loading`; `runInitialFetch`
                // flips it to `loaded`/`error` when the fetch settles.
                void this.runInitialFetch(topic, initialFetch);
            } else {
                // No initial fetch on this topic (e.g. clerk / timed dashboards
                // that read items purely via reconcile + broadcasts) — there's
                // nothing to wait for, so the topic is immediately `loaded`.
                this.setFetchState(topic, 'loaded', null);
            }
        }

        let disposed = false;
        return () => {
            if (disposed) return;
            disposed = true;
            const e = this.entries.get(topic);
            if (!e) return;
            e.listeners.delete(listener);
            e.subscribers--;
            if (e.subscribers === 0) {
                e.transportUnsubscribe?.();
                this.entries.delete(topic);
            }
        };
    }

    /**
     * Current items at `topic`, ascending by `(createdAt, id)`. The array
     * reference is stable across calls when nothing changed.
     */
    getItems(topic: string): readonly LedgerItem[] {
        return this.entries.get(topic)?.items ?? EMPTY_ITEMS;
    }

    /** Test-only inspection: number of active subscribers for `topic`. */
    subscriberCount(topic: string): number {
        return this.entries.get(topic)?.subscribers ?? 0;
    }

    /**
     * Current shared initial-fetch state for `topic` (BUG-803). Returns a
     * stable object reference (the entry's cached `fetchState`, or a frozen
     * `loading` default before the topic has an entry) so it is safe as a
     * `useSyncExternalStore` snapshot.
     */
    getFetchState(topic: string): Readonly<LedgerFetchState> {
        return this.entries.get(topic)?.fetchState ?? LOADING_FETCH_STATE;
    }

    /**
     * Observe `topic`'s fetch-state changes WITHOUT participating in the
     * item-subscription ref-count (no transport channel, no initial fetch, no
     * effect on `subscriberCount`). Used by `useLedgerFetchState`.
     */
    subscribeFetchState(topic: string, listener: () => void): Unsubscribe {
        let set = this.fetchStateListeners.get(topic);
        if (!set) {
            set = new Set();
            this.fetchStateListeners.set(topic, set);
        }
        set.add(listener);
        let disposed = false;
        return () => {
            if (disposed) return;
            disposed = true;
            const s = this.fetchStateListeners.get(topic);
            if (!s) return;
            s.delete(listener);
            if (s.size === 0) this.fetchStateListeners.delete(topic);
        };
    }

    /**
     * Fire the reconciler for every active topic. Provider mounts call this on
     * focus / visibilitychange / online / transport-reconnect, plus a periodic
     * safety-net tick, to close any gaps left by missed broadcast events or
     * transport reconnects.
     *
     * The tick interval belongs to the PROVIDER, not to this store, so don't
     * quote a number here — it goes stale (this comment said "30s" for months
     * after BL-563 moved it). For `LedgerStore` the caller is `LedgerContext`,
     * whose `RECONCILE_INTERVAL_MS` is 120s (BL-563, up from 30s). The sibling
     * stores are on their own cadence — `RealtimeLotsProvider` /
     * `RealtimeRegistrationsProvider` still tick at 30s, but they drive
     * `LotStateStore` / `RegistrationStateStore`, not this class.
     * `install-reconcile-triggers` additionally throttles the wake events to one
     * reconcile per `RECONCILE_THROTTLE_MS` (15s).
     */
    reconcileAll(): void {
        for (const topic of this.entries.keys()) {
            void this.reconcileTopic(topic);
        }
    }

    /** Reconcile a single topic. Exposed for tests; provider uses `reconcileAll`. */
    async reconcileTopic(topic: string): Promise<void> {
        const entry = this.entries.get(topic);
        if (!entry) return;
        // BL-2342 (ADR 066 — Offline Mode § 6.2): nothing is fetched while the
        // transport is suspended for offline mode.
        if (this.subscriber.isSuspended?.()) return;
        // An initial fetch held back by a suspension runs on the first
        // reconcile after it lifts, in place of a cursor reconcile.
        if (entry.heldInitialFetch) {
            const held = entry.heldInitialFetch;
            entry.heldInitialFetch = undefined;
            await this.runInitialFetch(topic, held);
            return;
        }
        if (!entry.reconciler || entry.reconciling) return;
        entry.reconciling = true;
        // Retry path (BUG-803): while the topic has not yet successfully loaded,
        // a reconcile IS the (re-)initial fetch — a manual "Try again" runs the
        // reconciler with no cursor. Surface its loading/error so the consumer's
        // spinner + error/retry track it. Once `loaded`, background reconciles
        // (focus / visibilitychange / interval) leave the state untouched so a
        // transient reconcile blip never blanks a populated list.
        if (entry.fetchState.status !== 'loaded') {
            this.setFetchState(topic, 'loading', null);
        }
        const cursorAtCall = entry.cursor;
        try {
            const items = await entry.reconciler(cursorAtCall);
            // Identity check: if every subscriber dropped mid-await the entry
            // was deleted (and any subscribe() after that re-created a fresh
            // one). Appending the old reconciler's items to that new entry
            // would seed it with stale state — bail instead.
            const current = this.entries.get(topic);
            if (current !== entry) return;
            this.appendMany(topic, items);
            // Server-authoritative contiguous read: everything strictly after
            // `cursorAtCall`. Advances even when every row was already held via
            // broadcast — see `advanceCursorFrom` (BUG-1281).
            this.advanceCursorFrom(topic, items);
            this.setFetchState(topic, 'loaded', null);
        } catch (err) {
            if (this.entries.get(topic) === entry && entry.fetchState.status !== 'loaded') {
                this.setFetchState(
                    topic,
                    'error',
                    err instanceof Error ? err : new Error(String(err)),
                );
            }
            // eslint-disable-next-line no-console
            console.error(
                ...formatRealtimeFailure(
                    '[LedgerContext] reconcile failed',
                    { topic: redactTopicSecret(topic) },
                    err,
                ),
            );
        } finally {
            // Same identity check on the in-flight flag — a stale entry must
            // not be left with `reconciling = true` (no harm if it was
            // deleted; only the original entry's flag matters).
            if (this.entries.get(topic) === entry) {
                entry.reconciling = false;
            }
        }
    }

    private openTransportSubscription(topic: string): void {
        const entry = this.entries.get(topic);
        if (!entry) return;
        // Per BL-547 the ledger channel carries TWO event names:
        //   - `ledger.appended` — strict dedupe-by-id (items immutable, per
        //     the LedgerItem docstring).
        //   - `ledger.amended` — upsert-by-id (BL-547). Mutations on the
        //     backing entity (status='outbid' / 'withdrawn' / etc.) re-broadcast
        //     the full row; the consumer's projection replaces the existing
        //     item with the same id so the rendered state stays current.
        // Each subscription returns its own unsubscribe; we close them both
        // when the last subscriber leaves the topic.
        const unsubAppended = this.subscriber.subscribe({
            topic,
            event: 'ledger.appended',
            handler: (payload) => {
                this.handleAppendedEvent(topic, payload);
            },
        });
        const unsubAmended = this.subscriber.subscribe({
            topic,
            event: 'ledger.amended',
            handler: (payload) => {
                this.handleAmendedEvent(topic, payload);
            },
        });
        entry.transportUnsubscribe = () => {
            unsubAppended();
            unsubAmended();
        };
    }

    /**
     * Transition `topic`'s shared fetch state and notify fetch-state observers.
     * No-op when nothing changed (so background reconciles on an already-loaded
     * topic — and topics with no fetch-state observers, e.g. clerk — produce no
     * spurious re-renders). Recreates the frozen object only on a real change so
     * `getFetchState` keeps returning a stable reference.
     */
    private setFetchState(
        topic: string,
        status: LedgerFetchStatus,
        error: Error | null,
    ): void {
        const entry = this.entries.get(topic);
        if (!entry) return;
        if (entry.fetchState.status === status && entry.fetchState.error === error) {
            return;
        }
        entry.fetchState = Object.freeze({ status, error });
        this.notifyFetchState(topic);
    }

    private notifyFetchState(topic: string): void {
        const set = this.fetchStateListeners.get(topic);
        if (!set) return;
        set.forEach((listener) => {
            listener();
        });
    }

    private async runInitialFetch<T extends LedgerItem>(
        topic: string,
        initialFetch: LedgerInitialFetcher<T>,
    ): Promise<void> {
        const entry = this.entries.get(topic);
        if (!entry) return;
        // BL-2342: suspended for offline mode — hold the fetch (the topic stays
        // `loading`) until the resume's `reconcileAll` runs it.
        if (this.subscriber.isSuspended?.()) {
            entry.heldInitialFetch = initialFetch as LedgerInitialFetcher<LedgerItem>;
            return;
        }
        try {
            const items = await initialFetch();
            // Identity check: if the entry was deleted (last subscriber left)
            // and a fresh one was created in its place, we must not seed it
            // with this old fetcher's items — bail.
            if (this.entries.get(topic) !== entry) return;
            this.appendMany(topic, items);
            // Server-authoritative contiguous read: the full history for this
            // topic, so its max IS the high-water mark (BUG-1281).
            this.advanceCursorFrom(topic, items);
            this.setFetchState(topic, 'loaded', null);
        } catch (err) {
            if (this.entries.get(topic) !== entry) return;
            // The fetcher signals failure by throwing; surface it to every
            // subscriber of this topic (BUG-803) so a late subscriber shows the
            // error/retry state instead of an unending spinner.
            this.setFetchState(
                topic,
                'error',
                err instanceof Error ? err : new Error(String(err)),
            );
            // eslint-disable-next-line no-console
            console.error(
                ...formatRealtimeFailure(
                    '[LedgerContext] initial fetch failed',
                    { topic: redactTopicSecret(topic) },
                    err,
                ),
            );
        }
    }

    private handleAppendedEvent(topic: string, payload: unknown): void {
        const event = payload as { item: LedgerItem };
        this.appendMany(topic, [event.item]);
        this.detectGapAndReconcile(topic, event.item);
    }

    /**
     * BL-1631 AC 2 — gap detection on a sequenced topic.
     *
     * Holding sequence N and receiving N+1, the item is simply appended (the
     * `appendMany` above already did it) and nothing else happens. Receiving
     * N+2 or beyond means at least one broadcast was missed, so exactly ONE
     * catch-up is issued; it asks for `seq > cursor`, which returns the missed
     * items and advances the cursor past them.
     *
     * Why the arrived item does not advance the cursor itself: the cursor is a
     * **contiguous** high-water mark (BUG-1281). Advancing it to N+2 on arrival
     * would move it PAST the missing N+1, and the catch-up that should have
     * recovered that item would never ask for it again. `appendMany`
     * deliberately does not touch the cursor; only `advanceCursorFrom` does,
     * and only for a server-authoritative contiguous read.
     *
     * Unsequenced topics are inert here — no `seq` means no gap to detect, which
     * is the pre-BL-1631 behaviour the toast ledger keeps.
     *
     * @sideEffect May invoke the topic's reconciler (one catch-up round-trip).
     */
    private detectGapAndReconcile(topic: string, arrived: LedgerItem): void {
        if (arrived.seq === undefined) return;
        const entry = this.entries.get(topic);
        if (!entry || !entry.reconciler) return;
        const cursor = entry.cursor;
        // No cursor yet — the initial fetch has not landed, and it will
        // establish contiguity on its own. Reconciling now would race it.
        if (cursor === undefined || !isSeqCursor(cursor)) return;
        if (arrived.seq <= cursor.seq + 1) return;
        // `reconcileTopic` no-ops while a reconcile is already in flight, so a
        // burst of out-of-order arrivals collapses to one round-trip rather
        // than one per item.
        void this.reconcileTopic(topic);
    }

    /**
     * Handle a `ledger.amended` event by upserting the item:
     *   - KNOWN id → replace the entry in `itemsById` AND in `items`,
     *     re-sort (the amended item's createdAt may stay the same as the
     *     existing item's, but defensively re-sort to match `appendMany`'s
     *     contract).
     *   - UNKNOWN id → append as if `ledger.appended` (handles
     *     amend-before-create out-of-order delivery on reconnect — the
     *     amended payload IS the latest state, dropping it would lose data).
     *
     * Fires listeners once per call so consumers re-render.
     *
     * @sideEffect Mutates `entry.items` / `entry.itemsById` / `entry.cursor`
     *   and notifies listeners. No-op when the topic has no subscribers.
     */
    private handleAmendedEvent(topic: string, payload: unknown): void {
        const entry = this.entries.get(topic);
        if (!entry) return;
        const event = payload as { item: LedgerItem };
        const incoming = event.item;
        const existing = entry.itemsById.get(incoming.id);
        if (existing === undefined) {
            // Unknown id — treat as an append (idempotency on out-of-order
            // delivery; reconnect replay may deliver an amend before the
            // create event on a brand-new tab).
            this.appendMany(topic, [incoming]);
            return;
        }
        entry.itemsById.set(incoming.id, incoming);
        // Re-create the array reference + re-sort so React sees a change and
        // the (createdAt, id) total ordering is preserved if the amended
        // payload's createdAt differs from the existing item's.
        entry.items = entry.items
            .map((item) => (item.id === incoming.id ? incoming : item))
            .sort(compareItems);
        // The cursor is NOT recomputed here (BUG-1281) — an amend is a live
        // broadcast frame, and only server reads may move the high-water mark.
        entry.listeners.forEach((listener) => {
            listener();
        });
    }

    /**
     * Merge `items` into the topic (dedupe by id) and notify listeners.
     *
     * Deliberately does NOT touch `entry.cursor` — see `advanceCursorFrom`.
     */
    private appendMany(topic: string, items: LedgerItem[]): void {
        const entry = this.entries.get(topic);
        if (!entry) return;
        const newItems: LedgerItem[] = [];
        for (const item of items) {
            if (!entry.itemsById.has(item.id)) {
                entry.itemsById.set(item.id, item);
                newItems.push(item);
            }
        }
        if (newItems.length === 0) return;
        // Re-create the array reference so React sees a change. Sort once
        // after the merge — incoming items are usually already sorted, so
        // worst-case is O(n log n) per append-batch, fast enough for any
        // realistic ledger size.
        entry.items = [...entry.items, ...newItems].sort(compareItems);
        entry.listeners.forEach((listener) => {
            listener();
        });
    }

    /**
     * Advance `topic`'s catch-up cursor to the maximum key in `batch` (BUG-1281).
     *
     * The cursor is a **server-authoritative contiguous high-water mark**: it
     * claims "everything up to here has been confirmed by a server read", not
     * "the highest-keyed row I happen to be holding". Only the two server reads
     * may move it — `runInitialFetch` (full history) and `reconcileTopic`
     * (everything strictly after the previous cursor). Both return result sets
     * that are contiguous from the previous cursor, which is exactly the claim
     * the cursor makes.
     *
     * Live broadcast frames must NOT move it. Broadcast is best-effort — that is
     * the whole reason reconcile exists — so a delivered frame certifies nothing
     * about rows beneath it. Advancing from a live frame permanently buries any
     * row that was never delivered and sorts lower, because `reconcileTopic`
     * asks the server for `(createdAt, id) > cursor` and can never reach back.
     * Reachable in one transaction: a bid and its proxy counter-bid publish
     * separate frames, share `created_at` exactly, and carry random UUIDs — so
     * receiving only the higher-keyed one is an ordinary outcome.
     *
     * Called separately from `appendMany` (rather than folded into it) because
     * `appendMany` early-returns when every row was already held. A reconcile
     * whose rows all arrived earlier via broadcast still *confirms contiguity*
     * and must advance the cursor — otherwise those rows are re-requested on
     * every reconcile for the life of the tab.
     *
     * @sideEffect Mutates `entry.cursor`. Never moves it backwards, and no-ops
     *   on an empty batch.
     */
    private advanceCursorFrom(topic: string, batch: readonly LedgerItem[]): void {
        const entry = this.entries.get(topic);
        if (!entry || batch.length === 0) return;
        let max = batch[0];
        for (const item of batch) {
            if (compareItems(item, max) > 0) max = item;
        }
        // Cursor shape follows the item's ordering key, so a sequenced topic
        // stores `{ seq }` and asks the server for `seq > n` on the next
        // reconcile — the comparison that closes BL-1631's commit-order hole.
        const next: LedgerCursor =
            max.seq !== undefined
                ? { seq: max.seq }
                : { createdAt: max.createdAt, id: max.id };
        if (entry.cursor && compareCursors(next, entry.cursor) <= 0) return;
        entry.cursor = next;
    }
}

const EMPTY_ITEMS: readonly LedgerItem[] = Object.freeze([]);
